/**
 * Base Matching Engine Interface
 * 
 * Abstract base class that defines the contract for matching engines
 * across different game modes. Each mode can implement its own
 * matching logic while maintaining a consistent interface.
 */

import { Grid, MatchGroup } from '../Grid'
import { Cell } from '../CellTypes'
import { GameBalanceManager } from '../GameBalanceManager'

/**
 * Result of processing matches in the game
 */
export interface MatchResult {
    matches: MatchGroup[]
    clearedCancer: Cell[]
    totalCellsCleared: number
    chainLevel: number
    scoreGained: number
    engraftmentGained: number
}

/**
 * Chain reaction information for cascade scoring
 */
export interface ChainReaction {
    level: number
    matches: MatchGroup[]
    totalScore: number
    totalEngraftment: number
}

/**
 * Abstract base class for matching engines
 * 
 * Provides common functionality and defines the interface
 * that all mode-specific matching engines must implement.
 */
export abstract class BaseMatchingEngine {
    protected grid: Grid
    protected chainMultiplier: number[]
    protected balanceManager?: GameBalanceManager

    constructor(grid: Grid, balanceManager?: GameBalanceManager) {
        this.grid = grid
        this.balanceManager = balanceManager
        // Default chain multipliers (can be overridden by subclasses)
        this.chainMultiplier = [1, 2, 4, 8, 16, 32, 64, 128, 256, 512]
    }

    /**
     * Main entry point: Process all matches and cascades
     * 
     * This must be implemented by each game mode to define
     * how matching and clearing works in that mode.
     */
    public abstract processMatches(): MatchResult

    /**
     * Calculate score and engraftment for matches at a given chain level
     * 
     * Can be overridden by subclasses to customize scoring logic
     */
    protected abstract calculateChainScore(
        matches: MatchGroup[],
        chainLevel: number
    ): { score: number; engraftment: number }

    /**
     * Get the chain multiplier for a given chain level
     * 
     * Can be overridden to customize chain bonus progression
     */
    protected getChainMultiplier(chainLevel: number): number {
        const index = Math.max(0, chainLevel - 1)
        return index < this.chainMultiplier.length
            ? this.chainMultiplier[index]
            : this.chainMultiplier[this.chainMultiplier.length - 1]
    }

    /**
     * Set custom chain multipliers
     * 
     * Allows modes to define their own chain bonus curves
     */
    public setChainMultipliers(multipliers: number[]): void {
        this.chainMultiplier = [...multipliers]
    }

    /**
     * Get current chain multipliers
     */
    public getChainMultipliers(): number[] {
        return [...this.chainMultiplier]
    }

    /**
     * Update balance manager (useful for dynamic difficulty)
     */
    public setBalanceManager(balanceManager: GameBalanceManager): void {
        this.balanceManager = balanceManager
    }
}
